import React, { useState } from "react";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Plus, Minus, CheckCircle2, Scale } from "lucide-react";
import { toast } from "sonner";

export default function ProductCard({ product }) {
  const { lang, t, addToCart } = useApp();
  const name = lang === "tr" ? product.name_tr : product.name_en;
  const desc = lang === "tr" ? product.description_tr : product.description_en;
  const isKg = product.sell_by === "kg";
  const [kgOpen, setKgOpen] = useState(false);

  const handleAdd = () => {
    if (isKg) {
      setKgOpen(true);
      return;
    }
    addToCart(product);
    toast.success(t("added"), { description: name, duration: 1200 });
  };

  const handleAddKg = (grams) => {
    addToCart(product, { grams });
    toast.success(t("added"), {
      description: `${name} — ${grams} gr`,
      duration: 1200,
    });
    setKgOpen(false);
  };

  return (
    <>
      <div
        data-testid={`product-card-${product.id}`}
        className="group bg-white rounded-2xl border border-[#E8E2D5] hover:border-[#2E5A36] hover:shadow-lg transition-all duration-300 hover:-translate-y-1 overflow-hidden flex flex-col"
      >
        <div className="relative aspect-square bg-[#FAF7F2] overflow-hidden">
          {product.image_url ? (
            <img
              src={product.image_url}
              alt={name}
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              loading="lazy"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-4xl">🛒</div>
          )}
          <div className="absolute top-3 left-3 flex flex-col gap-1.5">
            {product.is_weekly_deal && (
              <Badge className="bg-[#E5A024] hover:bg-[#E5A024] text-white border-0 font-heading">
                🔥 Hafta
              </Badge>
            )}
            {product.is_campaign && !product.is_weekly_deal && (
              <Badge className="bg-[#D9653B] hover:bg-[#D9653B] text-white border-0 font-heading">
                % İndirim
              </Badge>
            )}
            {product.featured && !product.is_campaign && !product.is_weekly_deal && (
              <Badge className="bg-[#2E5A36] hover:bg-[#2E5A36] text-white border-0 font-heading">
                ⭐ Öne Çıkan
              </Badge>
            )}
            {isKg && (
              <Badge
                data-testid={`kg-badge-${product.id}`}
                className="bg-[#1B2A22] hover:bg-[#1B2A22] text-[#FAF7F2] border-0 font-heading"
              >
                <Scale className="w-3 h-3 mr-1" /> KG
              </Badge>
            )}
          </div>
          {    product.akinsoft_synced && (
            <div
              data-testid={`ikizler-badge-${product.id}`}
              className="absolute bottom-3 left-3 bg-white/95 backdrop-blur px-2 py-1 rounded-full flex items-center gap-1 border border-[#2E5A36]/30"
              title="İkizler Market"
            >
              <CheckCircle2 className="w-3 h-3 text-[#2E5A36]" />
              <span className="text-[10px] font-heading font-bold uppercase tracking-wider text-[#2E5A36]">
                İkizler
              </span>
            </div>
          )}
        </div>

        <div className="p-4 flex flex-col gap-3 flex-1">
          <div className="flex-1">
            <h3 className="font-heading font-semibold text-base text-[#1B2A22] leading-snug line-clamp-2">
              {name}
            </h3>
            {desc && (
              <p className="text-xs text-[#1B2A22]/60 mt-1 line-clamp-2">{desc}</p>
            )}
          </div>

          <div className="flex items-center justify-between gap-2">
            <div className="flex flex-col">
              {product.brand && (
                <span
                  data-testid={`brand-${product.id}`}
                  className="text-[10px] font-heading font-bold uppercase tracking-wider text-[#D9653B]"
                >
                  {product.brand}
                </span>
              )}
              {product.old_price && product.old_price > product.price && (
                <span className="text-xs text-[#1B2A22]/50 font-mono line-through">
                  ₺{product.old_price.toFixed(2)}
                </span>
              )}
              <span
                data-testid={`product-price-${product.id}`}
                className="font-mono font-bold text-lg text-[#2E5A36]"
              >
                ₺{product.price.toFixed(2)}
              </span>
              <span className="text-[10px] text-[#1B2A22]/50 uppercase font-semibold">
                /{isKg ? "kg" : product.unit}
              </span>
            </div>
            <Button
              data-testid={`add-to-cart-btn-${product.id}`}
              onClick={handleAdd}
              className="bg-[#2E5A36] hover:bg-[#1B2A22] text-[#FAF7F2] rounded-full h-10 px-4 font-semibold shadow-sm active:scale-95 transition-all"
            >
              {isKg ? <Scale className="w-4 h-4 mr-1" /> : <Plus className="w-4 h-4 mr-1" />}
              {isKg ? t("choose_grams") : t("add_to_cart")}
            </Button>
          </div>
        </div>
      </div>

      {isKg && (
        <KgPickerDialog
          open={kgOpen}
          onOpenChange={setKgOpen}
          product={product}
          onConfirm={handleAddKg}
        />
      )}
    </>
  );
}

function KgPickerDialog({ open, onOpenChange, product, onConfirm }) {
  const { t, lang } = useApp();
  const name = lang === "tr" ? product.name_tr : product.name_en;
  const step = Number(product.step_grams || 50);
  const minG = Number(product.min_grams || 100);
  const [grams, setGrams] = useState(Number(product.default_grams || 500));

  const presets = [250, 500, 1000, 2000].filter((g) => g >= minG);
  const price = Number(product.price);
  const total = (grams * price) / 1000;

  const bump = (delta) => {
    setGrams((g) => Math.max(minG, Number(g) + delta));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid={`kg-picker-${product.id}`}
        className="bg-[#FAF7F2] border-[#E8E2D5] max-w-md"
      >
        <DialogHeader>
          <DialogTitle className="font-heading text-xl flex items-center gap-2">
            <Scale className="w-5 h-5 text-[#2E5A36]" /> {name}
          </DialogTitle>
          <DialogDescription>
            {t("kg_helper", { price: price.toFixed(2), min: minG })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="grid grid-cols-4 gap-2">
            {presets.map((g) => (
              <button
                key={g}
                data-testid={`kg-preset-${g}`}
                onClick={() => setGrams(g)}
                className={`h-11 rounded-xl font-heading font-bold text-sm border transition-all ${
                  grams === g
                    ? "bg-[#2E5A36] text-[#FAF7F2] border-[#2E5A36]"
                    : "bg-white text-[#1B2A22] border-[#E8E2D5] hover:border-[#2E5A36]"
                }`}
              >
                {g >= 1000 ? `${g / 1000} kg` : `${g} gr`}
              </button>
            ))}
          </div>

          <div className="bg-white border border-[#E8E2D5] rounded-2xl p-4">
            <p className="text-[10px] uppercase font-heading font-bold text-[#2E5A36] mb-2">
              {t("custom_amount")}
            </p>
            <div className="flex items-center justify-center gap-3">
              <button
                data-testid="kg-minus"
                onClick={() => bump(-step)}
                className="w-11 h-11 rounded-full bg-white border border-[#E8E2D5] hover:border-[#2E5A36] flex items-center justify-center"
              >
                <Minus className="w-4 h-4" />
              </button>
              <div className="flex items-center gap-2">
                <Input
                  data-testid="kg-grams-input"
                  type="number"
                  min={minG}
                  step={step}
                  value={grams}
                  onChange={(e) => setGrams(Math.max(minG, Number(e.target.value) || minG))}
                  className="w-28 h-11 text-center text-xl font-mono font-bold bg-[#FAF7F2]"
                />
                <span className="font-heading font-bold text-[#1B2A22]/60">gr</span>
              </div>
              <button
                data-testid="kg-plus"
                onClick={() => bump(step)}
                className="w-11 h-11 rounded-full bg-[#2E5A36] text-[#FAF7F2] hover:bg-[#1B2A22] flex items-center justify-center"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
            <p className="text-xs text-[#1B2A22]/60 text-center mt-2">
              {t("kg_step_hint", { step })}
            </p>
          </div>

          <div className="bg-[#2E5A36]/10 border border-[#2E5A36]/30 rounded-2xl p-4 flex items-center justify-between">
            <div>
              <p className="text-[10px] uppercase font-heading font-bold text-[#2E5A36]">
                {t("line_total")}
              </p>
              <p
                data-testid="kg-computed-total"
                className="font-mono font-extrabold text-2xl text-[#1B2A22]"
              >
                ₺{total.toFixed(2)}
              </p>
            </div>
            <p className="text-sm text-[#1B2A22]/70 font-mono">
              {grams} gr × ₺{price.toFixed(2)}/kg
            </p>
          </div>

          <Button
            data-testid={`kg-add-confirm-${product.id}`}
            onClick={() => onConfirm(grams)}
            className="w-full h-12 bg-[#D9653B] hover:bg-[#B4522F] text-white rounded-full font-heading font-bold"
          >
            <Plus className="w-4 h-4 mr-1" /> {t("add_to_cart")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
